import React, { useEffect, useMemo, useRef, useState } from 'react'
import {
  Outlet,
  useNavigate,
  useOutletContext,
  useLocation,
} from 'react-router-dom'
import styled from 'styled-components'
import axios from '../../../config/axios'
import RegisterVehicleModal from './Vehicles/RegisterVehicleModal/index'
import { Content } from './style'

import {
  OptionsButton,
  LeftSide,
  RightSide,
  SectionName,
  SubHeaderContainer,
  Tab,
  TabsDiv,
  ButtonCreate,
} from '../../../components/styles/subheader-style'

import filter from '../../../assets/equipment/TableMovementHeader/filter.svg'
import settings from '../../../assets/equipment/TableMovementHeader/settings.svg'
import truck from '../../../assets/equipment/TableMovementHeader/truck.svg'

import {
  GlobalTable,
  HeaderLine,
  Th,
  Tr,
  Td,
  TdButtons,
  DivInTd,
} from '../../../components/styles/table-style'

interface TypeMainOutlet {
  search: string
}

export interface EquipmentOutletType {
  tabIndex: number
  setTabIndex: React.Dispatch<React.SetStateAction<number>>
  refresh: boolean
  search: string
  Styled: {
    GlobalTable: typeof GlobalTable
    HeaderLine: typeof HeaderLine
    Th: typeof Th
    Tr: typeof Tr
    Td: typeof Td
    TdButtons: typeof TdButtons
    DivInTd: typeof DivInTd
    InfoMessage: any
  }
}

const InfoMessage = styled.div`
  padding: 40px;
  text-align: center;
  color: #8c98a4;
  font-size: 16px;
`

interface PeriodicMaintenanceData {
  maintenanceName: string
  periodicityValue: number | ''
  periodicityUnit: string
}

interface VehicleDataToSend {
  plate: string
  model: string
  year: number
  capacity: string
  bodyType: string
  restriction: string | null
  periodicMaintenances: PeriodicMaintenanceData[]
}

export const EquipmentSubHeader: React.FC<{
  tabIndexActived: number
  setRefresh: React.Dispatch<React.SetStateAction<boolean>>
  onOpenRegisterVehicleModal: () => void
}> = ({ tabIndexActived, setRefresh, onOpenRegisterVehicleModal }) => {
  const navigate = useNavigate()

  const [isFiltersMenuOpen, setIsFiltersMenuOpen] = useState<boolean>(false)
  const filterMenuRef = useRef<HTMLDivElement>(null)

  const tabs = [
    { path: '', name: 'Máquinas', icon: settings },
    { path: 'vehicles', name: 'Veículos', icon: truck },
  ]

  const handleRegisterClick = () => {
    if (tabIndexActived === 0) {
      navigate('/panel/equipment/register')
    } else if (tabIndexActived === 1) {
      onOpenRegisterVehicleModal()
    }
  }

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        filterMenuRef.current &&
        !filterMenuRef.current.contains(event.target as Node)
      ) {
        setIsFiltersMenuOpen(false)
      }
    }
    document.addEventListener('mousedown', handleClickOutside)
    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [])

  return (
    <SubHeaderContainer>
      <LeftSide>
        <SectionName>Equipamentos</SectionName>
        <TabsDiv>
          {tabs.map((tab, index) => (
            <Tab
              key={index}
              $isFirst={index === 0}
              $isLast={index === tabs.length - 1}
              $isActive={index === tabIndexActived}
              onClick={() => {
                navigate(`/panel/equipment/${tab.path}`)
              }}
            >
              <img
                src={tab.icon}
                alt={tab.name}
                style={{ marginRight: '8px' }}
              />
              {tab.name}
            </Tab>
          ))}
        </TabsDiv>
      </LeftSide>
      <RightSide>
        <OptionsButton
          onClick={() => setIsFiltersMenuOpen(!isFiltersMenuOpen)}
          ref={filterMenuRef}
        >
          <img src={filter} alt='filter-icon' />
        </OptionsButton>
        <ButtonCreate onClick={handleRegisterClick}>
          + Cadastrar {tabIndexActived === 0 ? 'máquina' : 'veículo'}
        </ButtonCreate>
      </RightSide>
    </SubHeaderContainer>
  )
}

const EquipmentContent = () => {
  const [tabIndex, setTabIndex] = useState<number>(0)
  const [refresh, setRefresh] = useState(false)
  const { search } = useOutletContext<TypeMainOutlet>()
  const location = useLocation()

  const [isRegisterVehicleModalOpen, setIsRegisterVehicleModalOpen] =
    useState(false)

  const handleOpenRegisterVehicleModal = () => {
    setIsRegisterVehicleModalOpen(true)
  }

  const handleCloseRegisterVehicleModal = () => {
    setIsRegisterVehicleModalOpen(false)

    setRefresh((prev) => !prev)
  }

  const handleSaveRegisterVehicleModal = async (
    vehicleData: VehicleDataToSend,
  ): Promise<boolean> => {
    const token = localStorage.getItem('token')
    if (!token) {
      alert('Sessão expirada ou não logado. Faça login novamente.')
      return false
    }
    if (!vehicleData.plate || !vehicleData.model || !vehicleData.year) {
      alert('Por favor, preencha os campos obrigatórios: Placa, Modelo e Ano.')
      return false
    }

    if (vehicleData.periodicMaintenances) {
      for (const item of vehicleData.periodicMaintenances) {
        if (
          !item.maintenanceName ||
          typeof item.periodicityValue !== 'number' ||
          item.periodicityValue <= 0 ||
          !item.periodicityUnit
        ) {
          alert(
            `Preencha todos os campos obrigatórios para cada manutenção periódica (Nome, Valor da Periodicidade e Unidade).`,
          )
          return false
        }
      }
    }

    try {
      const response = await axios.post(
        `${process.env.REACT_APP_GATEWAY_BASE_URL}/equipment/vehicles`,
        vehicleData,
        {
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          },
        },
      )

      if (response.status === 201 || response.status === 200) {
        alert('Veículo cadastrado com sucesso!')
        handleCloseRegisterVehicleModal()
        setRefresh((prev) => !prev)
        return true
      } else {
        alert(
          `Erro ao cadastrar veículo: ${
            response.statusText || 'Resposta inesperada do servidor.'
          }`,
        )
        return false
      }
    } catch (error) {
      console.error('Erro ao salvar veículo:', error)
      let errorMessage = 'Ocorreu um erro inesperado ao salvar o veículo.'
      if (axios.isAxiosError(error)) {
        errorMessage = error.response?.data?.message || error.message
        if (error.response?.status === 401) {
          errorMessage = 'Não autorizado. Por favor, faça login novamente.'
        } else if (error.response?.status === 400) {
          errorMessage = `Dados inválidos: ${
            error.response?.data?.details ||
            error.response?.data?.message ||
            'Verifique os campos preenchidos.'
          }`
        }
      }
      alert(`Erro ao cadastrar veículo: ${errorMessage}`)
      return false
    }
  }

  const outletContext: EquipmentOutletType = useMemo(
    () => ({
      tabIndex,
      setTabIndex,
      refresh,
      search,
      Styled: {
        GlobalTable,
        HeaderLine,
        Th,
        Tr,
        Td,
        TdButtons,
        DivInTd,
        InfoMessage,
      },
    }),
    [tabIndex, refresh, search],
  )

  useEffect(() => {
    if (location.pathname.includes('/equipment/vehicles')) {
      setTabIndex(1)
    } else if (location.pathname.includes('/equipment')) {
      setTabIndex(0)
    } else {
      setTabIndex(0)
    }
  }, [location.pathname])

  const shouldShowSubHeader =
    location.pathname === '/panel/equipment' ||
    location.pathname === '/panel/equipment/' ||
    location.pathname === '/panel/equipment/vehicles' ||
    location.pathname === '/panel/equipment/vehicles/'

  return (
    <>
      {shouldShowSubHeader && (
        <EquipmentSubHeader
          tabIndexActived={tabIndex}
          setRefresh={setRefresh}
          onOpenRegisterVehicleModal={handleOpenRegisterVehicleModal}
        />
      )}
      <Content>
        <Outlet context={outletContext} />
      </Content>
      <RegisterVehicleModal
        isOpen={isRegisterVehicleModalOpen}
        onClose={handleCloseRegisterVehicleModal}
        onSave={handleSaveRegisterVehicleModal}
      />
    </>
  )
}

export default EquipmentContent

